// src/components/maintenance/utils/csvGenerator.js
import { supabaseClient } from '../../supabase';
import JSZip from 'jszip';

/**
 * Fetch all records from a table using pagination
 */
async function fetchAllRecords(table, selectQuery, orderBy = 'id') {
  const pageSize = 1000;
  let allRecords = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error } = await supabaseClient
      .from(table)
      .select(selectQuery)
      .order(orderBy, { ascending: true })
      .range(from, to);

    if (error) throw error;

    if (data && data.length > 0) {
      allRecords = allRecords.concat(data);
      page++;
      
      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      }
    } else {
      hasMore = false;
    }
  }

  console.log(`Fetched ${allRecords.length} records from ${table}`);
  return allRecords;
}

/**
 * Generate teachers CSV
 */
async function generateTeachersCSV() {
  const teachers = await fetchAllRecords(
    'teachers',
    `
      id,
      first_name,
      last_name,
      email,
      mobile,
      role,
      class_id,
      grade,
      classes (
        class_code
      )
    `,
    'last_name'
  );

  const rows = [];

  // Header - ADD ROLE
  rows.push([
    'Teacher ID',
    'First Name',
    'Last Name',
    'Email',
    'Mobile',
    'Role',  // ← NEW
    'Class Code (Owner)',
  ]);

  // Data rows - ADD ROLE
  teachers.forEach(teacher => {
    rows.push([
      teacher.id || '',
      teacher.first_name || '',
      teacher.last_name || '',
      teacher.email || '',
      teacher.mobile || '',
      teacher.role || 'teacher',  // ← NEW
      teacher.classes?.class_code || '',
    ]);
  });

  // Convert to CSV string with UTF-8 BOM
  const csvContent = rows.map(row =>
    row.map(cell => escapeCSVCell(cell)).join(',')
  ).join('\n');

  return '\ufeff' + csvContent;
}

/**
 * Generate classes CSV
 */
async function generateClassesCSV() {
  const classes = await fetchAllRecords(
    'classes',
    '*',
    'class_code'
  );

  const rows = [];

  // Header
  rows.push([
    'Class ID',
    'Class Code',
    'Grade',
  ]);

  // Data rows
  classes.forEach(classItem => {
    rows.push([
      classItem.id || '',
      classItem.class_code || '',
      classItem.grade !== null ? classItem.grade : '',
    ]);
  });

  // Convert to CSV string with UTF-8 BOM
  const csvContent = rows.map(row =>
    row.map(cell => escapeCSVCell(cell)).join(',')
  ).join('\n');

  return '\ufeff' + csvContent;
}

/**
 * Generate students and parents CSV
 */
async function generateStudentsParentsCSV() {
  // Fetch all non-alumni students
  const students = await fetchAllRecordsWithFilter(
    'students',
    `
      id,
      first_name,
      last_name,
      household_hash,
      class_id,
      classes (
        class_code,
        grade
      )
    `,
    'last_name',
    { column: 'grade', operator: 'neq', value: 99 }
  );

  // Fetch all parents
  const parents = await fetchAllRecords(
    'parents',
    '*',
    'id'
  );

  // Group parents by household_hash
  const householdMap = new Map();
  parents.forEach(parent => {
    if (!householdMap.has(parent.household_hash)) {
      householdMap.set(parent.household_hash, []);
    }
    householdMap.get(parent.household_hash).push(parent);
  });

  // Find maximum number of parents in any household
  let maxParents = 0;
  for (const [_, householdParents] of householdMap) {
    if (householdParents.length > maxParents) {
      maxParents = householdParents.length;
    }
  }
  
  console.log(`Maximum parents in any household: ${maxParents}`);

  // Build CSV with dynamic parent columns
  const rows = [];

  // Header - dynamic based on max parents
  const header = [
    'Student ID',
    'First Name',
    'Last Name',
    'Class Code',
  ];
  
  for (let i = 1; i <= maxParents; i++) {
    header.push(
      `Parent ${i} First Name`,
      `Parent ${i} Last Name`,
      `Parent ${i} Email`,
      `Parent ${i} Mobile`
    );
  }
  
  rows.push(header);

  // Data rows
  students.forEach(student => {
    const householdParents = householdMap.get(student.household_hash) || [];
    const classCode = student.classes?.class_code || '';

    const row = [
      student.id || '',
      student.first_name || '',
      student.last_name || '',
      classCode,
    ];
    
    // Add all parents for this household
    for (let i = 0; i < maxParents; i++) {
      const parent = householdParents[i] || {};
      row.push(
        parent.first_name || '',
        parent.last_name || '',
        parent.email || '',
        parent.mobile || ''
      );
    }
    
    rows.push(row);
  });

  // Convert to CSV string with UTF-8 BOM
  const csvContent = rows.map(row =>
    row.map(cell => escapeCSVCell(cell)).join(',')
  ).join('\n');

  return '\ufeff' + csvContent;
}

/**
 * Fetch all records with a filter using pagination
 */
async function fetchAllRecordsWithFilter(table, selectQuery, orderBy, filter) {
  const pageSize = 1000;
  let allRecords = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    let query = supabaseClient
      .from(table)
      .select(selectQuery)
      .order(orderBy, { ascending: true })
      .range(from, to);

    // Apply filter
    if (filter) {
      if (filter.operator === 'neq') {
        query = query.neq(filter.column, filter.value);
      } else if (filter.operator === 'eq') {
        query = query.eq(filter.column, filter.value);
      } else if (filter.operator === 'gt') {
        query = query.gt(filter.column, filter.value);
      } else if (filter.operator === 'lt') {
        query = query.lt(filter.column, filter.value);
      }
      // Add more operators as needed
    }

    const { data, error } = await query;

    if (error) throw error;

    if (data && data.length > 0) {
      allRecords = allRecords.concat(data);
      page++;

      // If we got less than pageSize, we've reached the end
      if (data.length < pageSize) {
        hasMore = false;
      }
    } else {
      hasMore = false;
    }
  }

  console.log(`Fetched ${allRecords.length} records from ${table} with filter`);
  return allRecords;
}

/**
 * Escape CSV cell value
 */
function escapeCSVCell(value) {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // If contains comma, quotes, or newline, wrap in quotes and escape existing quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Parse CSV string into array of objects
 */
export function parseCSV(csvText) {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV file is empty or invalid');
  }

  const headers = parseCSVLine(lines[0]);
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length === headers.length) {
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });
      rows.push(row);
    }
  }

  return rows;
}

/**
 * Parse a single CSV line, handling quoted values
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  result.push(current.trim());

  return result;
}

/**
 * Identify CSV type by headers
 */
export function identifyCSVType(csvText) {
  const firstLine = csvText.split('\n')[0].toLowerCase();

  if (firstLine.includes('teacher id') && firstLine.includes('email')) {
    return 'teachers';
  } else if (firstLine.includes('class id') && firstLine.includes('class code')) {
    return 'classes';
  } else if (firstLine.includes('student id') && firstLine.includes('parent 1 email')) {
    return 'students_parents';
  }

  throw new Error('Unknown CSV type - check your column headers');
}

// Export the individual CSV generation functions
export { generateTeachersCSV, generateClassesCSV, generateStudentsParentsCSV };